import moment from 'moment';
import { CompanyFiscalYear } from 'api';
import { ISO_DATE_FORMAT, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';

const MONTHS_PER_QUARTER = 3;

// A fiscal year end is only usable once both of its optional parts are present.
export type DatedCompanyFiscalYear = CompanyFiscalYear & Required<Pick<CompanyFiscalYear, 'end_month' | 'end_day'>>;

export const isValidCompanyFiscalYear = (fye: unknown): fye is DatedCompanyFiscalYear =>
  typeof fye === 'object' &&
  fye !== null &&
  typeof (fye as CompanyFiscalYear).end_month === 'number' &&
  typeof (fye as CompanyFiscalYear).end_day === 'number';

/**
 * Returns the fiscal quarter end to measure next.
 *
 * Fiscal quarters end on the company's fiscal year end month and every third month after it.
 * A measurement date is a closed period, so the suggestion is the quarter AFTER the one holding it:
 * a company closing its year on 01/31 rolls a 12/31 measurement date to 04/30, not 03/31.
 * With no measurement date to roll from, the anchor is today, which is still an open period, so the
 * suggestion is the quarter end it is heading towards.
 *
 * @param {Object} fye - the company's fiscal year end, as end_month and end_day
 * @param {String} date - the measurement date to roll from, in YYYY-MM-DD format; defaults to today
 *
 * @return {String} the suggested measurement date in MM/DD/YYYY format
 */
const getSuggestedQuarterFiscalEndDate = (fye: CompanyFiscalYear, date: string | null = null): string => {
  if (!isValidCompanyFiscalYear(fye)) {
    throw new Error(
      'Invalid fiscal year end parameter. It should be an object with end_month and end_day properties (numbers).'
    );
  }

  if (date && (typeof date !== 'string' || !moment(date, ISO_DATE_FORMAT).isValid())) {
    throw new Error(`Invalid date parameter. It should be a string in ${ISO_DATE_FORMAT} format.`);
  }

  const anchor = date ? moment(date, ISO_DATE_FORMAT) : moment();

  // Walk forward from a full year back to the quarter end that closes the anchor's quarter.
  let quarterEnd = moment({ year: anchor.year() - 1, month: fye.end_month - 1 }).endOf('month');
  while (quarterEnd.isBefore(anchor, 'day')) {
    quarterEnd = quarterEnd.add(MONTHS_PER_QUARTER, 'months').endOf('month');
  }

  // A measurement date has already been measured, so roll one quarter past it; today has not.
  if (date) {
    quarterEnd = quarterEnd.add(MONTHS_PER_QUARTER, 'months').endOf('month');
  }

  return quarterEnd.format(MOMENT_DEFAULT_DATE_FORMAT);
};

export default getSuggestedQuarterFiscalEndDate;
